/// <reference path="../../../../../node_modules/kakao.maps.d.ts/@types/index.d.ts" />
import { CommonModule } from "@angular/common";
import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Output,
  ViewChild,
  input,
} from "@angular/core";
import { bounceAnimation } from "../../animations/bounce.animation";

@Component({
  selector: "app-map",
  standalone: true,
  imports: [CommonModule],
  templateUrl: "./map.component.html",
  styleUrls: ["./map.component.scss"],
})
export class MapComponent implements AfterViewInit {
  @Output() locationSelected = new EventEmitter<any>();
  @ViewChild("map") mapRef: ElementRef<HTMLElement> | undefined;

  locations = input<
    {
      address: string;
      overlay: string;
      category: string;
      locationId: string;
    }[]
  >([]);

  map: kakao.maps.Map | undefined;
  overlays: kakao.maps.CustomOverlay[] = [];
  selectedPlace = input<string>("");

  ngOnChanges() {
    if (this.overlays.length === 0) {
      return;
    }

    this.updateOverlays(this.selectedPlace());
  }

  ngAfterViewInit(): void {
    this.initialize(() => {
      if (this.locations().length > 0) {
        const initialLocationId = this.locations()[0].locationId;

        this.updateOverlays(initialLocationId);
      }
    });
  }

  async initialize(onComplete: () => void) {
    if (!this.mapRef || this.locations().length === 0) {
      onComplete();
      return;
    }

    const geocoder = new kakao.maps.services.Geocoder();

    geocoder.addressSearch(
      this.locations()[0].address,
      async (result, status) => {
        if (status === kakao.maps.services.Status.OK && result.length > 0) {
          const initialCoords = new kakao.maps.LatLng(
            parseFloat(result[0].y),
            parseFloat(result[0].x)
          );

          const options: kakao.maps.MapOptions = {
            center: initialCoords,
            level: 3,
          };

          this.map = new kakao.maps.Map(this.mapRef!.nativeElement, options);

          const bounds = new kakao.maps.LatLngBounds(); // 지도 영역 객체 생성

          // 위치 데이터 순차 처리
          for (let i = 0; i < this.locations().length; i++) {
            const location = this.locations()[i];
            await this.setAddressCenter(location);

            // 마커 좌표를 지도 영역에 추가
            const coords = await this.getLatLng(location.address);
            bounds.extend(coords);
          }

          // 모든 마커를 포함하는 영역으로 지도 축소 및 이동
          this.map.setBounds(bounds);

          onComplete();
        } else {
          console.error("지도 초기화 실패:", status);
          onComplete();
        }
      }
    );
  }

  getLatLng(address: string): Promise<kakao.maps.LatLng> {
    return new Promise((resolve, reject) => {
      const geocoder = new kakao.maps.services.Geocoder();

      geocoder.addressSearch(address, (result, status) => {
        if (status === kakao.maps.services.Status.OK && result.length > 0) {
          const coords = new kakao.maps.LatLng(
            parseFloat(result[0].y),
            parseFloat(result[0].x)
          );
          resolve(coords);
        } else {
          console.error("주소 변환 실패:", status);
          reject(status);
        }
      });
    });
  }

  setAddressCenter(location: {
    address: string;
    overlay: string;
    category: string;
    locationId: string;
  }): Promise<void> {
    return new Promise((resolve) => {
      const geocoder = new kakao.maps.services.Geocoder();

      geocoder.addressSearch(location.address, (result, status) => {
        if (status === kakao.maps.services.Status.OK && result.length > 0) {
          const coords = new kakao.maps.LatLng(
            parseFloat(result[0].y),
            parseFloat(result[0].x)
          );

          if (this.map) {
            this.addMarker(
              coords.getLat(),
              coords.getLng(),
              location.overlay,
              location.category,
              location.locationId
            );
          }
        } else {
          console.error("주소 검색 실패:", status);
        }

        resolve(); // 작업 완료
      });
    });
  }

  addMarker(
    lat: number,
    lng: number,
    overlayText: string,
    category: string,
    locationId: string
  ) {
    const markerImage = new kakao.maps.MarkerImage(
      "https://t1.daumcdn.net/localimg/localimages/07/mapapidoc/markerStar.png",
      new kakao.maps.Size(24, 35)
    );

    const marker = new kakao.maps.Marker({
      position: new kakao.maps.LatLng(lat, lng),
      image: markerImage,
      map: this.map,
    });

    marker.setVisible(false);

    if (this.map) marker.setMap(this.map);

    const overlayContent = document.createElement("div");
    overlayContent.style.cssText = `
      display: inline-flex; 
      flex-direction: column;
      align-items: flex-start;
      padding: 10px;
      padding-right: 24px;
      background: white;
      border: 1px solid rgba(204, 204, 204, 1);
      border-radius: 9999px;
      box-shadow: 0 2px 6px rgba(0, 0, 0, 0.15);
      white-space: nowrap; 
      position: relative;
      transition: border-color 0.3s ease;
    `;

    overlayContent.innerHTML = `
    <div class="flex gap-1 items-center shrink-1 flex-1">
      <img id="overlay-icon-${locationId}" src="icons/location_default.svg" class="size-4" />
      <div class="flex flex-col gap-1">
        <div id="overlay-title-${locationId}" style="font-size: 14px; color: #1F2937; font-weight: 600;">
          ${overlayText}
        </div>
        <div id="overlay-category-${locationId}" style="font-size: 10px; color: #6B7280; font-weight: 600;">
          ${category}
        </div>
      </div>
    </div>
  `;

    const tailBorderDiv = document.createElement("div");
    tailBorderDiv.id = `tail-border-${locationId}`;
    tailBorderDiv.style.cssText = `
      position: absolute;
      bottom: -12px;
      left: 50%;
      transform: translateX(-50%);
      width: 0;
      height: 0;
      border-style: solid;
      border-width: 12px 12px 0 12px; 
      border-color: rgba(204, 204, 204, 1) transparent transparent transparent;
    `;

    const tailInnerDiv = document.createElement("div");
    tailInnerDiv.style.cssText = `
      position: absolute;
      bottom: -12px;
      left: 50%;
      transform: translateX(-50%);
      width: 0;
      height: 0;
      border-style: solid;
      border-width: 12px 10.5px 0 10.5px;
      border-color: white transparent transparent transparent;
    `;

    overlayContent.appendChild(tailBorderDiv);
    overlayContent.appendChild(tailInnerDiv);

    overlayContent.appendChild(tailBorderDiv);
    overlayContent.appendChild(tailInnerDiv);

    const overlay = new kakao.maps.CustomOverlay({
      position: new kakao.maps.LatLng(lat, lng),
      content: overlayContent,
      yAnchor: 1.4,
      map: this.map,
    });

    this.overlays.push(overlay);

    // 클릭 이벤트 처리
    overlayContent.addEventListener("click", () => {
      this.updateOverlays(locationId);
      this.locationSelected.emit(locationId);
    });

    overlay.setZIndex(1); // 기본 zIndex 설정
  }

  updateOverlays(selectedId: string) {
    this.overlays.forEach((overlay, index) => {
      const location = this.locations()[index];
      const overlayElement = overlay.getContent() as HTMLElement;

      // 꼬리 요소 가져오기
      const tailBorderDiv = document.getElementById(
        `tail-border-${location.locationId}`
      );

      // 텍스트 요소 가져오기
      const overlayTitle = document.getElementById(
        `overlay-title-${location.locationId}`
      );
      const overlayCategory = document.getElementById(
        `overlay-category-${location.locationId}`
      );

      //맵 아이콘 요소 가져오기
      const overlayIcon = document.getElementById(
        `overlay-icon-${location.locationId}`
      ) as HTMLImageElement;

      if (location.locationId === selectedId) {
        bounceAnimation(overlayElement);

        // 선택된 오버레이 스타일 변경
        overlayElement.style.borderColor = "rgba(254, 50, 119, 1)";
        if (tailBorderDiv)
          tailBorderDiv.style.borderColor =
            "rgba(254, 50, 119, 1) transparent transparent transparent";

        // 선택된 텍스트 색상 변경
        if (overlayTitle) overlayTitle.style.color = "rgba(254, 50, 119, 1)";
        if (overlayCategory)
          overlayCategory.style.color = "rgba(254, 50, 119, 1)";

        if (overlayIcon) overlayIcon.src = "icons/location_active.svg";

        overlay.setZIndex(999);
      } else {
        // 기본 오버레이 스타일 변경
        overlayElement.style.borderColor = "rgba(204, 204, 204, 1)";
        if (tailBorderDiv)
          tailBorderDiv.style.borderColor =
            "rgba(204, 204, 204, 1) transparent transparent transparent";

        // 기본 텍스트 색상 복원
        if (overlayTitle) overlayTitle.style.color = "#1F2937";
        if (overlayCategory) overlayCategory.style.color = "#6B7280";

        if (overlayIcon) overlayIcon.src = "icons/location_default.svg";

        overlay.setZIndex(1);
      }
    });
  }
}
